/**
 * Viewport scaling utilities to ensure consistent display across different screen sizes
 */

import { useMediaQuery } from '@mui/material';
import { useEffect } from 'react';
import { isDevToolsActive } from '../utils/devToolsDetection';

/**
 * Hook to apply viewport scaling based on screen size
 * This helps maintain consistent visual appearance across different resolutions
 */
export const useViewportScaling = () => {
  const isExtraLargeScreen = useMediaQuery('(min-width:2000px)');
  
  useEffect(() => {
    // Apply scaling based on screen size
    const html = document.documentElement;
    
    // Check if we're in dev tools inspection mode using our utility
    if (isDevToolsActive()) {
      // Skip scaling if dev tools are active to prevent crashes
      return;
    }
    
    if (isExtraLargeScreen) {
      // For very large screens (e.g., 2560x1440)
      html.style.fontSize = '18px'; // Increase base font size
      
      // Add a class to the body for additional styling
      document.body.classList.add('large-viewport');
    } else {
      // For medium and smaller screens
      html.style.fontSize = '16px'; // Standard font size
      document.body.classList.remove('large-viewport');
    }
    
    return () => {
      // Cleanup
      html.style.fontSize = '';
      document.body.classList.remove('large-viewport');
    };
  }, [isExtraLargeScreen]);
};

/**
 * Component to apply viewport scaling
 */
export const ViewportScaler = ({ children }) => {
  useViewportScaling();
  return children;
};

export default ViewportScaler; 